<template>
    <div class="variance-box-wrapper" ref="wrapperRef">
        <VarianceGraph
            :data="props.data"
            :screenWidth="props.screenWidth"
            :shouldAnimate="animateTrigger"
            :isForShare="props.isForShare"
        />
        <div class="sub-title-wrap">
            <p class="title body-2">{{ props.data.GraphName }}</p>
            <ul class="user-info-chip-list">
                <CommChipList v-if="computedChipTextList" :chipTextList="computedChipTextList" />
            </ul>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import VarianceGraph from './VarianceGraph.vue';
import { getBodyTypeValue } from '@common/common/utils/utils';
import { CommChipList } from '@common/trend-report-common';
import type { VarianceVarianceResDto } from '@common/openapi-client/openapi';
import { useThrottleFn } from '@common/common/composables/useThrottleFn';

const props = defineProps<{
    data: VarianceVarianceResDto;
    screenWidth: number;
    isAdmin: boolean;
    isForShare?: boolean;
}>();

const animateTrigger = ref(false);
const wrapperRef = ref<HTMLDivElement | null>(null);

const computedBodyType = computed(() => {
    return (bodyType: string) => {
        return getBodyTypeValue(bodyType, 'bodyTypeKoreanName', '2040');
    };
});

const computedChipTextList = computed(() => {
    const filter = props.data.FilterInfo;
    const list: string[] = [];

    if (filter?.Gender) {
        list.push(filter.Gender === 'M' ? '남성' : '여성');
    }
    if (filter?.Age) {
        list.push(`${filter.Age}세`);
    }
    if (filter?.BodyType) {
        list.push(computedBodyType.value(filter.BodyType));
    }

    return list;
});

const scrollParentSelector = computed(() => {
    return props.isAdmin ? '.mobile-screen' : '.wrapper';
});

const startAnimation = () => {
    animateTrigger.value = false;
    requestAnimationFrame(() => {
        animateTrigger.value = true;
    });
};

const rawScrollHandler = () => {
    const top = wrapperRef.value?.getBoundingClientRect().top ?? 0;
    const trigger = window.innerHeight * 0.85;

    if (top < trigger) {
        startAnimation();
        document.querySelector(scrollParentSelector.value)?.removeEventListener('scroll', throttledScrollHandler);
    }
};

const throttledScrollHandler = useThrottleFn(rawScrollHandler, 150);

onMounted(() => {
    document.querySelector(scrollParentSelector.value)?.addEventListener('scroll', throttledScrollHandler);
});

onUnmounted(() => {
    document.querySelector(scrollParentSelector.value)?.removeEventListener('scroll', throttledScrollHandler);
});
</script>

<style scoped lang="scss">
.cluster-2d-graph {
    position: relative;
    width: 100%;
    height: auto;
    display: flex;
    justify-content: center;
    align-items: center;
}

.big-title {
    font-size: 2rem;
    font-weight: 700;
    line-height: 100%;
    color: $primary-black;
    margin-bottom: 0.8rem;

    .highlight-text {
        text-decoration: underline;
    }
}

.image-canvas,
.animation-canvas {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: auto;
}

.animation-canvas {
    pointer-events: none;
}

.sub-title-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 1rem;

    .title {
        color: $grey-7;
        margin-right: 0.5rem;
        font-size: 1.2rem;
    }
}
</style>
